import { Component } from '@theme/component';

const REF_STORAGE_KEY = 'som:waitlist:ref';
const SIGNUP_STORAGE_KEY = 'som:waitlist:signup';
const CODE_PATTERN = /^[a-z0-9]{4,14}$/;

/**
 * Deterministic referral code from an email address.
 * Two independent 32-bit FNV/Murmur-style passes, base36, 8 chars.
 * scripts/waitlist-queue.mjs recomputes the identical code server-side,
 * so the code never needs to be stored on the customer record.
 * @param {string} email
 * @returns {string}
 */
export function referralCode(email) {
  const s = email.trim().toLowerCase();
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5 ^ 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) >>> 0;
  }
  return (h1.toString(36) + h2.toString(36)).slice(0, 8);
}

/**
 * Waitlist signup with referral tagging.
 *
 * On load: captures `?ref=` into localStorage so the attribution survives
 * browsing before signup, and writes the tag payload into the hidden
 * `contact[tags]` input. On submit: stores the visitor's own email-derived
 * code so the success state (a full-page reload via the Shopify customer
 * form) can render their personal link. Attribution lives entirely in
 * customer tags: `waitlist` plus `ref-<code of referrer>`.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [tagsInput]
 * @property {HTMLElement} [referralPanel]
 * @property {HTMLElement} [referralLink]
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLElement} [copyLabel]
 * @property {HTMLElement} [copySuccess]
 *
 * @extends {Component<Refs>}
 */
class WaitlistSignup extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();
    this.#captureReferrer();
    this.#applyTags();
    this.#form = this.querySelector('form');
    this.#form?.addEventListener('submit', this.#handleSubmit);
    this.#hydrateSuccessState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  /** Persist a well-formed ?ref= parameter so attribution survives navigation. */
  #captureReferrer() {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref')?.toLowerCase();
      if (ref && CODE_PATTERN.test(ref)) {
        localStorage.setItem(REF_STORAGE_KEY, ref);
      }
    } catch {
      /* storage unavailable: signup still works, attribution is lost */
    }
  }

  #storedReferrer() {
    try {
      const ref = localStorage.getItem(REF_STORAGE_KEY)?.toLowerCase();
      return ref && CODE_PATTERN.test(ref) ? ref : null;
    } catch {
      return null;
    }
  }

  /** @param {string} [ownCode] */
  #applyTags(ownCode) {
    const { tagsInput } = this.refs;
    if (!tagsInput) return;
    const tags = ['waitlist'];
    const ref = this.#storedReferrer();
    if (ref && ref !== ownCode) tags.push(`ref-${ref}`);
    tagsInput.value = tags.join(',');
  }

  #handleSubmit = () => {
    const email = this.refs.emailInput?.value;
    if (!email) return;
    const code = referralCode(email);
    // Self-referral guard: someone opening their own link should not tag themselves.
    this.#applyTags(code);
    try {
      localStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify({ code }));
    } catch {
      /* success state falls back to plain confirmation */
    }
  };

  /**
   * After the customer form round-trips, show the personal link if this
   * browser performed the signup. Without the stored code (other device,
   * cleared storage) the panel stays hidden rather than showing a wrong link.
   */
  #hydrateSuccessState() {
    const { referralPanel, referralLink } = this.refs;
    if (!referralPanel || !referralLink) return;

    let code = null;
    try {
      code = JSON.parse(localStorage.getItem(SIGNUP_STORAGE_KEY) ?? 'null')?.code ?? null;
    } catch {
      code = null;
    }
    if (!code || !CODE_PATTERN.test(code)) return;

    const base = this.getAttribute('referral-base') || `${window.location.origin}${window.location.pathname}`;
    const url = `${base}${base.includes('?') ? '&' : '?'}ref=${code}`;
    referralLink.textContent = url;
    this.#shareUrl = url;
    referralPanel.removeAttribute('hidden');
  }

  #shareUrl = '';

  copyLink() {
    if (!this.#shareUrl) return;
    navigator.clipboard?.writeText(this.#shareUrl).then(() => {
      this.refs.copyLabel?.classList.add('visually-hidden');
      this.refs.copySuccess?.classList.remove('visually-hidden');
      setTimeout(() => {
        this.refs.copyLabel?.classList.remove('visually-hidden');
        this.refs.copySuccess?.classList.add('visually-hidden');
      }, 2500);
    });
  }
}

if (!customElements.get('waitlist-signup')) {
  customElements.define('waitlist-signup', WaitlistSignup);
}
